<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InfoRow from './infoRow.vue'

import InfluencerBadgeIcon from '~/components/influencer/badgeIcon.vue'
import VTooltip from '~/components/ui/VTooltip.vue'

import { useComputeMismatches } from '~/composables/useComputeMismatches'
import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useGetHighestPriorityBadgeName } from '~/composables/useGetHighestPriorityBadgeName'

import { useDraftStore } from '~/stores/draft'
import { useInfluencersInteractionsStore } from '~/stores/influencersInteractions'
import { useMiscResizeStore } from '~/stores/miscResize'

import { getGrowthBookFeatureValue } from '~/helpers/GrowthBookHelpers'

import { isDefined } from '~/utils/type-guards'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { StoreInfluencerInteraction } from '~/types/influencerInteraction'
import type { MismatchType } from '~/types/influencerRecommendation'
import type { PositiveRatingCriterion } from '~/types/PositiveRatingCriterion'
import type { AverageRating } from '~/types/RatingContextBehaviors'

// BEGINNING OF AB TEST 'ratings-social-proof'
type RatingDisplay = 'stars' | 'satisfaction-rate'
interface SatisfactionRate {
  percentage?: number
  type?: AverageRating['type']
  ratersCount?: number
}
// END OF AB TEST 'ratings-social-proof'

type Props = {
  influencer: Influencer | StatsV3Influencer
  selected: boolean
}

const props = defineProps<Props>()

const { $growthBook } = useNuxtApp()
const { t } = useI18n()
const { daysLeft, curatorIsContest } = useCuratorContestsInfos(props.influencer)
const { computeMismatches } = useComputeMismatches()
const { mismatches } = computeMismatches('card', props.influencer)

const displayedBadgeName = useGetHighestPriorityBadgeName(
  props.influencer.tags.identity.influencer_badge,
)

const { id: DRAFT_ID } = storeToRefs(useDraftStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const { INF_ID_IS_IN: IS_INFLUENCER_IN_CART } = useDraftStore()
const { GET_INTERACTION_FROM_INFLUENCER_ID } = useInfluencersInteractionsStore()

const influencerFeedbackCount = ref(0)
const influencerInteractionSentTracksCount = ref(0)
const influencerInteractionSharedTracksCount = ref(0)

// BEGINNING OF AB TEST 'ratings-social-proof'
const shouldDisplayTooltipMessage = ref(false)
// END OF AB TEST 'ratings-social-proof'

// BEGINNING OF AB TEST 'ratings-social-proof'
function getRatingType(rawType?: string, context?: RatingDisplay): string {
  if (!rawType) return ''

  if (rawType === 'share')
    return context === 'stars' ? t('common.sharing') : t('common.aShare', 2)

  return context === 'stars'
    ? t(`common.${rawType}`)
    : `${t(`common.${rawType}`, 2)}`
}

function toggleTooltip(e: Event): void {
  if (SCREEN_WIDTH.value >= Breakpoints.md) return

  e.preventDefault()
  e.stopPropagation()
  shouldDisplayTooltipMessage.value = !shouldDisplayTooltipMessage.value
}

const ratingSocialProofTestCell = computed<
  'control' | 'satisfaction-rate' | 'stars'
>(() => getGrowthBookFeatureValue($growthBook, 'ratings-social-proof'))
const isInRatingSocialProofStarsTestCell = computed<boolean>(
  () => ratingSocialProofTestCell.value === 'stars',
)
const isInRatingSocialProofSatisfactionRateTestCell = computed<boolean>(
  () => ratingSocialProofTestCell.value === 'satisfaction-rate',
)
const starRatingType = computed<string>(() =>
  getRatingType(props.influencer.latest_stats?.stars_rate.type, 'stars'),
)
const satisfactionRate = computed<SatisfactionRate>(() => ({
  percentage: props.influencer.latest_stats?.satisfaction_rate.value,
  type: props.influencer.latest_stats?.satisfaction_rate.type,
  ratersCount: props.influencer.latest_stats?.satisfaction_rate.rating_number,
}))
const satisfactionRateDisplay = computed<string>(() => {
  if (!isDefined(satisfactionRate.value.percentage)) return ''

  const extraDetail =
    props.influencer.latest_stats?.stars_rate.type === 'overall'
      ? ` (${satisfactionRate.value.ratersCount})`
      : ''
  const satisfactionRatingType = getRatingType(
    satisfactionRate.value.type,
  ).toLowerCase()
  return t('draft.filters.influencers.satisfactionRate', {
    percentage: Math.round(satisfactionRate.value.percentage),
    type: `${satisfactionRatingType}${extraDetail}`,
  })
})
const doesCuratorMeetRatingsSocialProofConditions = computed<boolean>(() => {
  // TODO: ask about this condition because it seems opposite to what it should be
  if (influencerInteractionSentTracksCount.value) return false

  return (props.influencer.latest_stats?.stars_rate.rating_number ?? 0) >= 5
})
const shouldShowAverageStarRating = computed<boolean>(() => {
  if (!isInRatingSocialProofStarsTestCell.value) return false

  return doesCuratorMeetRatingsSocialProofConditions.value
})
const shouldShowSatisfactionRate = computed<boolean>(() => {
  if (!isInRatingSocialProofSatisfactionRateTestCell.value) return false

  return doesCuratorMeetRatingsSocialProofConditions.value
})
// END OF AB TEST 'ratings-social-proof'

const curatorIsInCart = computed<boolean>(() =>
  IS_INFLUENCER_IN_CART(props.influencer.id),
)
const criteria = computed<PositiveRatingCriterion[]>(
  () => props.influencer.latest_stats?.best_rating_criteria || [],
)
const formattedFeedbackCount = computed<string>(() =>
  influencerFeedbackCount.value < 100
    ? t('influencer.card.info.feedbacks.lessThan100')
    : t(
        'influencer.card.info.feedbacks.moreThanX',
        influencerFeedbackCount.value,
      ),
)
const formattedCriteria = computed<string>(() => {
  if (criteria.value.length === 0) return ''

  const criteriaLocales = criteria.value
    .map((criterion) => {
      const camelCaseCriterion = setUnderscoreToCamelCase(criterion)
      return t(`influencer.card.info.criteria.${camelCaseCriterion}`)
    })
    .join(', ')

  return `${t('influencer.card.info.artistsFeedback')} ${criteriaLocales}`
})
const hasBadge = computed<boolean>(
  () =>
    props.influencer.tags.identity.influencer_badge?.length > 0 ||
    Number(props.influencer.cost) > 2 ||
    false,
)
const showCriteriaSocialProof = computed<boolean>(() => !!DRAFT_ID.value)

function computeFeedbackRoundedCount(): number {
  const feedbackCount = props.influencer.latest_stats?.feedback_count

  return feedbackCount ? Math.floor(feedbackCount / 100) * 100 : 0
}

function computeInfluencerInteraction(): null | StoreInfluencerInteraction {
  return GET_INTERACTION_FROM_INFLUENCER_ID(props.influencer.id) ?? null
}

function mismatchIconClass(type: MismatchType): string {
  if (type === 'duplicate') return 'fa-solid:exclamation-triangle'
  else return 'fa-solid:times'
}

function setUnderscoreToCamelCase(text: string): string {
  return text.replace(/[-_]([a-z])/g, function (g) {
    return g[1].toUpperCase()
  })
}

onMounted(() => {
  influencerInteractionSharedTracksCount.value =
    computeInfluencerInteraction()?.shared ?? 0
  influencerInteractionSentTracksCount.value =
    computeInfluencerInteraction()?.track_sent ?? 0
  influencerFeedbackCount.value = computeFeedbackRoundedCount() ?? 0
})
</script>

<template>
  <div
    class="tw-relative tw-grid tw-w-full tw-grid-cols-1 tw-grid-rows-[16px] tw-gap-sm"
    :class="{ isContest: curatorIsContest(influencer), selected }"
  >
    <InfoRow
      v-if="
        !!mismatches.length &&
        mismatches[0].mismatchText &&
        !mismatches[0].weakMismatch
      "
      :icon-class="mismatchIconClass(mismatches[0].type)"
      :text="mismatches[0].mismatchText"
      typographic-component="Sm600Error"
    />
    <InfoRow
      v-else-if="
        curatorIsContest(influencer) &&
        daysLeft &&
        daysLeft >= 0 &&
        daysLeft <= 10
      "
      icon-class="fa6-regular:clock"
      :text="$t('influencer.card.info01', daysLeft)"
      typographic-component="Sm400Pink"
    />
    <InfluencerBadgeIcon
      v-else-if="hasBadge"
      v-slot="{ iconClass, iconText, name }"
      :influencer="influencer"
      :badge-name="displayedBadgeName"
    >
      <InfoRow
        :icon-class="iconClass"
        :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
        :text="name ? iconText : ''"
      />
    </InfluencerBadgeIcon>
    <InfoRow
      v-else-if="curatorIsContest(influencer)"
      icon-class="fa6-regular:heart"
      :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
      :text="$t('influencer.card.info.preferences')"
    />
    <InfoRow
      v-else
      icon-class="fa6-regular:comment"
      :text="formattedFeedbackCount"
      :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
    />

    <!-- SECOND LINE (doesn't exist for influencers cards of type contest) -->
    <div v-if="!curatorIsContest(influencer)">
      <InfoRow
        v-if="influencerInteractionSharedTracksCount"
        icon-class="fa6-solid:hands-clapping"
        :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
        :text="
          $t('influencer.card.info.interactions.sharedTracks', {
            amount: influencerInteractionSharedTracksCount,
          })
        "
      />
      <InfoRow
        v-else-if="influencerInteractionSentTracksCount"
        icon-class="fa6-solid:circle-chevron-right"
        :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
        :text="
          $t(
            'influencer.card.info.interactions.sentTracks',
            influencerInteractionSentTracksCount,
          )
        "
      />
      <!-- BEGINNING OF AB TEST 'ratings-social-proof' -->
      <div
        v-else-if="shouldShowAverageStarRating"
        class="tw-flex tw-items-center"
      >
        <LazyStarSlider
          v-if="shouldShowAverageStarRating"
          is-rated
          :model-value="influencer.latest_stats?.stars_rate.value || 0"
          :are-stars-colored="!curatorIsInCart"
          :star-width="18"
          :star-gap="2"
          :left-space="0"
        />
        <span class="tw-ml-1 tw-text-sm tw-capitalize">
          {{ starRatingType }}
        </span>
        <span class="tw-ml-2 tw-text-sm tw-text-discrete2">
          ({{ influencer.latest_stats?.stars_rate.rating_number || 0 }})
        </span>
        <VTooltip
          class="tw-z-10 tw-ml-2 tw-mt-[2px]"
          :force-to-show="shouldDisplayTooltipMessage"
          @click="toggleTooltip"
        >
          <template #default>
            <i class="fa-solid fa-circle-info tw-text-sm tw-text-gray-500" />
          </template>
          <template #tooltipText>
            {{
              $t(
                `draft.filters.influencers.rating.${influencer.latest_stats?.stars_rate.type}`,
              )
            }}
          </template>
          <template v-if="SCREEN_WIDTH < Breakpoints.md" #cta>
            <button
              type="button"
              class="tw-mx-auto tw-mt-2 tw-block focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-white"
              @click="toggleTooltip"
            >
              <span
                class="tw-text-sm tw-font-medium tw-text-white tw-underline"
              >
                {{ $t('common.okGotIt') }}
              </span>
            </button>
          </template>
        </VTooltip>
      </div>
      <InfoRow
        v-else-if="shouldShowSatisfactionRate"
        icon-class="fa6-regular:star"
        :text="satisfactionRateDisplay"
        is-two-lines
      />
      <!-- END OF AB TEST 'ratings-social-proof' -->
      <InfoRow
        v-else-if="!!criteria.length && showCriteriaSocialProof"
        icon-class="fa6-regular:heart"
        is-two-lines
        :text="formattedCriteria"
        :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
      />
      <InfoRow
        v-else
        :typographic-component="selected ? 'Sm400Gray' : 'Sm400Black'"
        icon-class="fa6-regular:heart"
        :text="$t('influencer.card.info.preferences')"
      />
    </div>
  </div>
</template>
